// Hierarchical agent structure diagram — ported from prototype's OrgChart.
// Shows the agent's specialists and the MCPs they delegate to.

interface Specialist {
  role: string;
  toolMcp: string;
  toolName: string;
}

interface Props {
  leader?: string;
  leaderHint?: string;
  specialists?: Specialist[];
}

const DEFAULT_SPECIALISTS: Specialist[] = [
  { role: 'Retrieval Specialist', toolMcp: 'DMS MCP', toolName: 'dms.search' },
  { role: 'Submission Specialist', toolMcp: 'OCR MCP', toolName: 'ocr.extract' },
  { role: 'Q&A Specialist', toolMcp: 'KB MCP', toolName: 'kb.sources' },
  { role: 'Approval Specialist', toolMcp: 'Workflow MCP', toolName: 'workflow.create' },
  { role: 'Security Guard', toolMcp: 'Identity MCP', toolName: 'id.verify' },
];

function Node({
  label,
  sub,
  monoSub = false,
  accent = false,
}: {
  label: string;
  sub?: string;
  monoSub?: boolean;
  accent?: boolean;
}) {
  return (
    <div
      className="rounded-[10px] px-3 py-2 text-center text-[12.5px]"
      style={{
        background: accent ? 'var(--accent-soft)' : 'var(--surface)',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: accent
          ? 'color-mix(in oklab, var(--accent) 40%, var(--border))'
          : 'var(--border-strong)',
      }}
    >
      <div className="font-semibold">{label}</div>
      {sub && (
        <div
          className="text-text-3 text-[11px] mt-0.5"
          style={{ fontFamily: monoSub ? 'var(--font-mono)' : 'var(--font-ui)' }}
        >
          {sub}
        </div>
      )}
    </div>
  );
}

function VerticalLine() {
  return <div className="w-px h-3.5 mx-auto" style={{ background: 'var(--border-strong)' }} />;
}

export function OrgChart({
  leader = 'Conversation Leader',
  leaderHint = 'owns final response',
  specialists = DEFAULT_SPECIALISTS,
}: Props) {
  const cols = Math.min(specialists.length, 5);

  return (
    <div>
      <div className="flex justify-center">
        <Node label={leader} sub={leaderHint} accent />
      </div>
      <div className="w-px h-4 mx-auto" style={{ background: 'var(--border-strong)' }} />
      <div className="h-px mx-[9%]" style={{ background: 'var(--border-strong)' }} />
      <div
        className="grid gap-3 pt-0"
        style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
      >
        {specialists.slice(0, cols).map((s) => (
          <div key={s.role}>
            <VerticalLine />
            <div className="flex flex-col gap-0 items-stretch">
              <Node label={s.role} />
              <VerticalLine />
              <Node label={s.toolMcp} sub={s.toolName} monoSub />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
